import { useEffect, useState } from "react";
import { api, errDetail, fmtDate } from "../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { useApp } from "../lib/store";
import { Bot, Sparkles, Play, Pencil, History, BarChart3, MessageSquare, Archive, ArchiveRestore, RotateCcw } from "lucide-react";

const CAT_BADGE = {
  crm: "bg-blue-100 text-blue-700",
  srm: "bg-amber-100 text-amber-800",
  allgemein: "bg-slate-100 text-slate-700",
};
const CAT_LABEL = { crm: "CRM", srm: "SRM", allgemein: "Allgemein" };

function Section({ icon: Icon, title, children, action }) {
  return (
    <div className="border-t border-gray-100 pt-4 mt-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-[#1B2A4A]">
          {Icon && <Icon className="h-4 w-4 text-[#F5A623]" strokeWidth={2} />}{title}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

export function AiAgentDetailDialog({ agentId, open, onOpenChange, onChanged }) {
  const { perms, user } = useApp();
  const isAdmin = !!(perms?.is_admin || user?.role === "admin");
  const [agent, setAgent] = useState(null);
  const [models, setModels] = useState([]);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [testInput, setTestInput] = useState("");
  const [testOutput, setTestOutput] = useState(null);
  const [testing, setTesting] = useState(false);
  const [budgetDraft, setBudgetDraft] = useState(null);
  const [savingBudget, setSavingBudget] = useState(false);

  const load = () => {
    if (!agentId) return;
    api.get(`/ai-agents/${agentId}`).then((r) => setAgent(r.data)).catch((e) => toast.error(errDetail(e)));
  };
  useEffect(() => {
    if (open && agentId) {
      setAgent(null); setEditing(false); setTestInput(""); setTestOutput(null);
      load();
      api.get("/ai-agents/models").then((r) => setModels(r.data || [])).catch(() => {});
    }
  }, [open, agentId]);

  const startEdit = () => {
    setDraft({
      system_prompt: agent.system_prompt || "",
      behavior_notes: agent.behavior_notes || "",
      model_key: `${agent.model_provider}|${agent.model_name}`,
    });
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const [model_provider, model_name] = (draft.model_key || "|").split("|");
      await api.put(`/ai-agents/${agentId}`, {
        system_prompt: draft.system_prompt,
        behavior_notes: draft.behavior_notes,
        model_provider, model_name,
      });
      toast.success("Änderungen gespeichert");
      setEditing(false);
      load(); onChanged?.();
    } catch (e) { toast.error(errDetail(e)); }
    setSaving(false);
  };

  const toggleStatus = async (checked) => {
    try {
      await api.put(`/ai-agents/${agentId}`, { status: checked ? "active" : "inactive" });
      toast.success(checked ? "Agent aktiviert" : "Agent deaktiviert");
      load(); onChanged?.();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const toggleArchive = async () => {
    try {
      await api.post(`/ai-agents/${agentId}/${agent.archived ? "unarchive" : "archive"}`);
      toast.success(agent.archived ? "Agent wiederhergestellt" : "Agent archiviert");
      load(); onChanged?.();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const restoreVersion = async (historyId) => {
    try {
      const r = await api.post(`/ai-agents/${agentId}/restore/${historyId}`);
      setAgent(r.data);
      toast.success("Frühere Prompt-Version wiederhergestellt");
      onChanged?.();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const saveBudget = async () => {
    setSavingBudget(true);
    try {
      await api.put(`/ai-agents/${agentId}`, {
        monthly_budget_usd: Number(budgetDraft.monthly_budget_usd) || 0,
        budget_enforcement: budgetDraft.budget_enforcement || "warn",
      });
      toast.success("Budget gespeichert");
      setBudgetDraft(null);
      load(); onChanged?.();
    } catch (e) { toast.error(errDetail(e)); }
    setSavingBudget(false);
  };

  const runTest = async () => {
    if (!testInput.trim()) { toast.error("Bitte geben Sie eine Beispieleingabe ein"); return; }
    setTesting(true); setTestOutput(null);
    try {
      const r = await api.post(`/ai-agents/${agentId}/test`, { input: testInput });
      setTestOutput(r.data.output);
    } catch (e) { toast.error(errDetail(e)); }
    setTesting(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="ai-agent-detail-dialog">
        {!agent ? (
          <div className="py-10 text-center text-gray-400">Lädt…</div>
        ) : (
          <>
            <DialogHeader>
              <div className="flex items-start gap-3">
                <div className="h-11 w-11 rounded-xl bg-[#1B2A4A] flex items-center justify-center shrink-0">
                  <Bot className="h-5 w-5 text-[#F5A623]" strokeWidth={2} />
                </div>
                <div className="min-w-0">
                  <DialogTitle className="text-[#1B2A4A]" data-testid="ai-agent-detail-name">{agent.name}</DialogTitle>
                  <DialogDescription className="mt-0.5">{agent.short_description}</DialogDescription>
                  <div className="flex flex-wrap items-center gap-1.5 mt-2">
                    <Badge className={`${CAT_BADGE[agent.category] || CAT_BADGE.allgemein} hover:opacity-100`}>{CAT_LABEL[agent.category] || agent.category}</Badge>
                    <Badge variant="outline" className="gap-1 border-gray-300 text-gray-600"><Sparkles className="h-3 w-3" />{agent.model_badge}</Badge>
                    <Badge className={agent.status === "active" ? "bg-green-100 text-green-700 hover:bg-green-100" : "bg-gray-200 text-gray-600 hover:bg-gray-200"} data-testid="ai-agent-detail-status">
                      {agent.status === "active" ? "Aktiv" : "Inaktiv"}
                    </Badge>
                    {agent.archived && <Badge className="bg-gray-800 text-white hover:bg-gray-800" data-testid="ai-agent-detail-archived">Archiviert</Badge>}
                  </div>
                </div>
              </div>
            </DialogHeader>

            {isAdmin && (
              <div className="flex items-center justify-between rounded-lg bg-gray-50 border border-gray-200 px-3 py-2">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-gray-700">Agent aktiv</span>
                  <Switch checked={agent.status === "active"} onCheckedChange={toggleStatus} disabled={agent.archived} data-testid="ai-agent-status-toggle" />
                </div>
                <Button size="sm" variant="outline" onClick={toggleArchive} data-testid="ai-agent-archive-btn"
                  className={`h-8 gap-1 ${agent.archived ? "border-[#1B2A4A] text-[#1B2A4A]" : "border-gray-300 text-gray-600"}`}>
                  {agent.archived ? <><ArchiveRestore className="h-3.5 w-3.5" />Wiederherstellen</> : <><Archive className="h-3.5 w-3.5" />Archivieren</>}
                </Button>
              </div>
            )}

            {/* Zweck / Einsatzkontext */}
            <Section title="Zweck & Einsatzkontext">
              <p className="text-sm text-gray-600 leading-relaxed">{agent.purpose || "—"}</p>
            </Section>

            {/* System-Prompt + Verhalten + Modell (admin editierbar) */}
            <Section title="System-Prompt & Verhalten"
              action={isAdmin && !editing && (
                <Button size="sm" variant="outline" onClick={startEdit} data-testid="ai-agent-edit-btn" className="h-8 gap-1 border-gray-300">
                  <Pencil className="h-3.5 w-3.5" />Bearbeiten
                </Button>
              )}>
              {editing ? (
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">System-Prompt</label>
                    <Textarea value={draft.system_prompt} onChange={(e) => setDraft((d) => ({ ...d, system_prompt: e.target.value }))}
                      rows={8} className="mt-1 font-mono text-xs" data-testid="ai-agent-prompt-input" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Verhaltensbeschreibung</label>
                    <Textarea value={draft.behavior_notes} onChange={(e) => setDraft((d) => ({ ...d, behavior_notes: e.target.value }))}
                      rows={3} className="mt-1 text-sm" data-testid="ai-agent-behavior-input" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Modell & Provider</label>
                    <Select value={draft.model_key} onValueChange={(v) => setDraft((d) => ({ ...d, model_key: v }))}>
                      <SelectTrigger className="mt-1" data-testid="ai-agent-model-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {models.map((m) => (
                          <SelectItem key={`${m.provider}|${m.model}`} value={`${m.provider}|${m.model}`}>{m.label} · {m.provider}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button size="sm" variant="ghost" onClick={() => setEditing(false)} data-testid="ai-agent-cancel-btn">Abbrechen</Button>
                    <Button size="sm" onClick={save} disabled={saving} data-testid="ai-agent-save-btn" className="bg-[#1B2A4A] hover:bg-[#1B2A4A]/90">
                      {saving ? "Speichert…" : "Speichern"}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <pre className="text-xs bg-[#0f172a] text-slate-100 rounded-lg p-3 whitespace-pre-wrap font-mono max-h-64 overflow-y-auto" data-testid="ai-agent-prompt-view">{agent.system_prompt}</pre>
                  <div>
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Verhalten</div>
                    <p className="text-sm text-gray-600 leading-relaxed">{agent.behavior_notes || "—"}</p>
                  </div>
                  <div className="text-xs text-gray-500">Modell: <span className="font-medium text-gray-700">{agent.model_label}</span> ({agent.model_provider})</div>
                </div>
              )}
            </Section>

            {/* Beispiel */}
            <Section icon={MessageSquare} title="Beispiel-Interaktion">
              <div className="rounded-lg border border-gray-200 divide-y divide-gray-100 text-sm">
                <div className="p-3"><span className="text-[10px] uppercase tracking-widest text-gray-400 font-semibold">Eingabe</span><p className="mt-1 text-gray-700 whitespace-pre-wrap">{agent.example_input || "—"}</p></div>
                <div className="p-3 bg-amber-50/40"><span className="text-[10px] uppercase tracking-widest text-gray-400 font-semibold">Ausgabe</span><p className="mt-1 text-gray-700 whitespace-pre-wrap">{agent.example_output || "—"}</p></div>
              </div>
            </Section>

            {/* Testbereich */}
            <Section icon={Play} title="Testbereich">
              <p className="text-xs text-gray-400 mb-2">Testen Sie den Agenten mit einer Beispieleingabe. Es wird kein echter Workflow ausgelöst.</p>
              <Textarea value={testInput} onChange={(e) => setTestInput(e.target.value)} rows={3}
                placeholder="Beispieleingabe eingeben…" className="text-sm" data-testid="ai-agent-test-input" />
              <div className="flex justify-end mt-2">
                <Button size="sm" onClick={runTest} disabled={testing} data-testid="ai-agent-test-run-btn" className="bg-[#F5A623] hover:bg-[#e0961a] text-[#1B2A4A] font-semibold gap-1">
                  <Play className="h-3.5 w-3.5" />{testing ? "Wird ausgeführt…" : "Testen"}
                </Button>
              </div>
              {testOutput != null && (
                <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3" data-testid="ai-agent-test-output">
                  <span className="text-[10px] uppercase tracking-widest text-gray-400 font-semibold">Antwort</span>
                  <p className="mt-1 text-sm text-gray-700 whitespace-pre-wrap">{testOutput}</p>
                </div>
              )}
            </Section>

            {/* Nutzung */}
            {agent.usage && (agent.usage.calls_30d > 0 || agent.usage.last_used) && (
              <Section icon={BarChart3} title="Nutzungsstatistik (30 Tage)">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm" data-testid="ai-agent-usage-stats">
                  <div><div className="text-2xl font-bold text-[#1B2A4A]">{agent.usage.calls_30d}</div><div className="text-xs text-gray-400">Aufrufe</div></div>
                  <div><div className="text-2xl font-bold text-[#1B2A4A]">{(agent.usage.tokens_total ?? 0).toLocaleString("de-DE")}</div><div className="text-xs text-gray-400">Tokens (geschätzt)</div></div>
                  <div><div className="text-2xl font-bold text-[#1B2A4A]" data-testid="ai-agent-usage-cost">${(agent.usage.cost_usd ?? 0).toFixed(4)}</div><div className="text-xs text-gray-400">geschätzte Kosten</div></div>
                  <div><div className="text-sm font-medium text-[#1B2A4A] pt-1.5">{agent.usage.last_used ? fmtDate(agent.usage.last_used) : "—"}</div><div className="text-xs text-gray-400">Letzte Nutzung</div></div>
                </div>
                <p className="text-[11px] text-gray-400 mt-2">Token-Zahlen sind geschätzt (~4 Zeichen/Token); Kosten sind ein Näherungswert je Modell.</p>
              </Section>
            )}

            {/* Monatsbudget */}
            <Section icon={BarChart3} title="Monatsbudget"
              action={isAdmin && !budgetDraft && (
                <Button variant="outline" size="sm" data-testid="ai-agent-budget-edit-btn"
                  onClick={() => setBudgetDraft({ monthly_budget_usd: agent.budget?.budget_usd || 0, budget_enforcement: agent.budget?.enforcement || "warn" })}>
                  <Pencil className="h-3.5 w-3.5 mr-1" />Bearbeiten
                </Button>
              )}>
              {budgetDraft ? (
                <div className="space-y-3" data-testid="ai-agent-budget-editor">
                  <div className="flex flex-wrap gap-4 items-end">
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">Monatsbudget (USD, 0 = kein Limit)</label>
                      <input type="number" min="0" step="0.01" data-testid="ai-agent-budget-input"
                        className="h-9 w-40 rounded-md border border-gray-300 px-3 text-sm"
                        value={budgetDraft.monthly_budget_usd}
                        onChange={(e) => setBudgetDraft((p) => ({ ...p, monthly_budget_usd: e.target.value }))} />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">Bei 100 %</label>
                      <Select value={budgetDraft.budget_enforcement} onValueChange={(v) => setBudgetDraft((p) => ({ ...p, budget_enforcement: v }))}>
                        <SelectTrigger className="h-9 w-48" data-testid="ai-agent-budget-enforcement"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="warn">Nur warnen (weiterlaufen)</SelectItem>
                          <SelectItem value="block">Hart stoppen (sperren)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={saveBudget} disabled={savingBudget} data-testid="ai-agent-budget-save-btn"
                      className="bg-[#1B2A4A] hover:bg-[#243759] text-white">Speichern</Button>
                    <Button size="sm" variant="ghost" onClick={() => setBudgetDraft(null)}>Abbrechen</Button>
                  </div>
                </div>
              ) : agent.budget?.has_budget ? (
                <div data-testid="ai-agent-budget-status">
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-gray-600">${(agent.budget.spent_usd || 0).toFixed(4)} / ${agent.budget.budget_usd.toFixed(2)}</span>
                    <span className={`font-semibold ${agent.budget.hard_stop ? "text-red-600" : agent.budget.soft_warning ? "text-amber-600" : "text-green-700"}`} data-testid="ai-agent-budget-pct">{agent.budget.pct}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                    <div className={`h-full ${agent.budget.hard_stop ? "bg-red-500" : agent.budget.soft_warning ? "bg-amber-500" : "bg-green-500"}`}
                      style={{ width: `${Math.min(agent.budget.pct, 100)}%` }} />
                  </div>
                  <p className="text-[11px] text-gray-400 mt-2">
                    Regel bei 100 %: {agent.budget.enforcement === "block" ? "hart stoppen (Workflow gesperrt)" : "nur warnen (läuft weiter)"}.
                    {agent.budget.soft_warning && !agent.budget.hard_stop && " Warnschwelle 80 % erreicht."}
                    {agent.budget.hard_stop && " Budget erschöpft."}
                  </p>
                </div>
              ) : (
                <p className="text-sm text-gray-400" data-testid="ai-agent-budget-none">Kein Monatsbudget gesetzt (keine Begrenzung).</p>
              )}
            </Section>

            {/* Verlauf */}
            <Section icon={History} title="Änderungsverlauf">
              {(!agent.history || agent.history.length === 0) ? (
                <p className="text-sm text-gray-400">Noch keine Änderungen erfasst.</p>
              ) : (
                <div className="space-y-2" data-testid="ai-agent-history">
                  {agent.history.map((h) => (
                    <details key={h.id} className="rounded-lg border border-gray-200 bg-white text-sm">
                      <summary className="cursor-pointer px-3 py-2 flex items-center justify-between hover:bg-gray-50">
                        <span className="text-gray-700 flex items-center gap-1"><History className="h-3.5 w-3.5 text-gray-400" />{h.changed_by_name}</span>
                        <span className="text-xs text-gray-400 flex items-center gap-1">{fmtDate(h.changed_at)}<span className="text-[#1B2A4A]">· Version ansehen</span></span>
                      </summary>
                      <div className="px-3 pb-3 space-y-2">
                        <div><div className="text-[10px] uppercase tracking-widest text-gray-400 font-semibold mb-1">Vorheriger Prompt</div>
                          <pre className="text-xs bg-gray-50 rounded p-2 whitespace-pre-wrap font-mono max-h-40 overflow-y-auto">{h.previous_prompt || "—"}</pre></div>
                        {isAdmin && (
                          <div className="flex justify-end">
                            <Button size="sm" variant="outline" onClick={() => restoreVersion(h.id)} data-testid={`ai-agent-restore-${h.id}`} className="h-7 gap-1 border-gray-300 text-gray-600 text-xs">
                              <RotateCcw className="h-3 w-3" />Diese Version wiederherstellen
                            </Button>
                          </div>
                        )}
                      </div>
                    </details>
                  ))}
                </div>
              )}
            </Section>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
